import React, { useState, useEffect, useCallback } from 'react';
import './AtlasPanel.css';

const API = process.env.REACT_APP_SERVER_URL || '';

const AtlasPanel = ({ character, onClose, onOpenGuide }) => {
  const [atlasData, setAtlasData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedZone, setSelectedZone] = useState(null);
  const [hoveredZone, setHoveredZone] = useState(null);

  const fetchAtlas = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/atlas`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.error) {
        setError(data.error);
        return;
      }
      setAtlasData(data);
      // Auto-select current zone
      if (data.character.currentZone) {
        setSelectedZone(data.character.currentZone);
      }
    } catch (err) {
      setError('Failed to load atlas data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAtlas();
  }, [fetchAtlas]);

  if (loading) {
    return (
      <div className="atlas-overlay" onClick={onClose}>
        <div className="atlas-panel" onClick={e => e.stopPropagation()}>
          <div className="atlas-loading">
            <div className="atlas-loading-icon">🗺️</div>
            <div>Charting the world...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="atlas-overlay" onClick={onClose}>
        <div className="atlas-panel" onClick={e => e.stopPropagation()}>
          <div className="atlas-header">
            <h2 className="atlas-title">Atlas</h2>
            <button className="atlas-close" onClick={onClose}>✕</button>
          </div>
          <div className="atlas-error">{error}</div>
        </div>
      </div>
    );
  }

  const { zones, summary, character: charInfo } = atlasData;
  const selected = selectedZone ? zones.find(z => z.id === selectedZone) : null;

  // Calculate grid bounds for rendering
  const minRow = Math.min(...zones.map(z => z.row));
  const maxRow = Math.max(...zones.map(z => z.row));
  const minCol = Math.min(...zones.map(z => z.col));
  const maxCol = Math.max(...zones.map(z => z.col));
  const gridRows = maxRow - minRow + 1;
  const gridCols = maxCol - minCol + 1;

  // Build a lookup for zone positions
  const zoneGrid = {};
  zones.forEach(z => {
    zoneGrid[`${z.row - minRow},${z.col - minCol}`] = z;
  });

  // Build connection lines
  const connections = [];
  const seen = new Set();
  zones.forEach(z => {
    (z.connections || []).forEach(cId => {
      const key = [z.id, cId].sort().join('-');
      if (seen.has(key)) return;
      seen.add(key);
      const target = zones.find(t => t.id === cId);
      if (!target) return;
      connections.push({
        from: { row: z.row - minRow, col: z.col - minCol },
        to: { row: target.row - minRow, col: target.col - minCol },
        discovered: z.isDiscovered && target.isDiscovered
      });
    });
  });

  const typeLabels = {
    city: 'City',
    town: 'Town',
    wilderness: 'Wilderness',
    dungeon: 'Dungeon',
    boss: 'Boss Zone',
    landmark: 'Landmark'
  };

  const tierColors = { 1: '#4fc3f7', 2: '#66bb6a', 3: '#ffa726', 4: '#ab47bc', 5: '#ef5350' };
  const tierNames = { 0: 'Normal', 1: 'Adventurer', 2: 'Veteran', 3: 'Champion', 4: 'Heroic', 5: 'Mythic' };
  const tierIcons = { 0: '—', 1: '⚔️', 2: '🛡️', 3: '🏆', 4: '💎', 5: '🔥' };

  return (
    <div className="atlas-overlay" onClick={onClose}>
      <div className="atlas-panel" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="atlas-header">
          <h2 className="atlas-title">🗺️ Atlas of Loria</h2>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {onOpenGuide && (
              <button
                type="button"
                onClick={() => onOpenGuide('atlas')}
                style={{
                  background: 'rgba(79, 195, 247, 0.12)',
                  border: '1px solid rgba(79, 195, 247, 0.28)',
                  color: '#9fdcf7',
                  borderRadius: '8px',
                  padding: '6px 10px',
                  cursor: 'pointer'
                }}
              >
                Guide
              </button>
            )}
            <button className="atlas-close" onClick={onClose}>✕</button>
          </div>
        </div>

        {/* Stats Bar */}
        <div className="atlas-stats-bar">
          <div className="atlas-stat">
            <span className="atlas-stat-label">Level</span>
            <span className="atlas-stat-value">{charInfo.level}</span>
          </div>
          <div className="atlas-stat">
            <span className="atlas-stat-label">World Tier</span>
            <span className="atlas-stat-value" style={{ color: tierColors[charInfo.worldTier] || '#aaa' }}>
              {tierIcons[charInfo.worldTier]} {tierNames[charInfo.worldTier]}
            </span>
          </div>
          <div className="atlas-stat">
            <span className="atlas-stat-label">Zones</span>
            <span className="atlas-stat-value">{summary.zonesDiscovered} / {summary.totalZones}</span>
          </div>
          <div className="atlas-stat">
            <span className="atlas-stat-label">Maps</span>
            <span className="atlas-stat-value">{summary.totalExplored} / {summary.totalMaps}</span>
          </div>
          {charInfo.delveStreak > 0 && (
            <div className="atlas-stat">
              <span className="atlas-stat-label">Delve Streak</span>
              <span className="atlas-stat-value" style={{ color: '#ff8a65' }}>🔥 {charInfo.delveStreak}</span>
            </div>
          )}
        </div>

        {/* Exploration Progress Bar */}
        <div className="atlas-progress-section">
          <div className="atlas-progress-header">
            <span>World Exploration</span>
            <span>{summary.explorationPct}%</span>
          </div>
          <div className="atlas-progress-bar">
            <div className="atlas-progress-fill" style={{ width: `${summary.explorationPct}%` }} />
          </div>
        </div>

        <div className="atlas-body">
          {/* Map Grid */}
          <div className="atlas-map-container">
            <svg className="atlas-connections" viewBox={`0 0 ${gridCols * 100} ${gridRows * 100}`} preserveAspectRatio="xMidYMid meet">
              {connections.map((c, i) => (
                <line
                  key={i}
                  x1={c.from.col * 100 + 50}
                  y1={c.from.row * 100 + 50}
                  x2={c.to.col * 100 + 50}
                  y2={c.to.row * 100 + 50}
                  className={`atlas-path ${c.discovered ? 'discovered' : 'undiscovered'}`}
                />
              ))}
            </svg>
            <div
              className="atlas-grid"
              style={{
                gridTemplateColumns: `repeat(${gridCols}, 1fr)`,
                gridTemplateRows: `repeat(${gridRows}, 1fr)`
              }}
            >
              {Array.from({ length: gridRows * gridCols }).map((_, idx) => {
                const r = Math.floor(idx / gridCols);
                const c = idx % gridCols;
                const zone = zoneGrid[`${r},${c}`];

                if (!zone) {
                  return <div key={idx} className="atlas-cell-empty" />;
                }

                const isSelected = selectedZone === zone.id;
                const isHovered = hoveredZone === zone.id;
                const isCurrent = zone.isCurrent;
                const levelOk = character.level >= zone.levelRange[0] - 5;
                // v622: per-zone fit indicator (matches detail-panel color logic)
                let fitClass = 'fit-good';
                if (character.level < zone.levelRange[0]) fitClass = 'fit-danger';
                else if (character.level > zone.levelRange[1] + 10) fitClass = 'fit-trivial';

                return (
                  <div
                    key={idx}
                    className={`atlas-zone-node ${zone.type} ${zone.isDiscovered ? 'discovered' : 'undiscovered'} ${isCurrent ? 'current' : ''} ${isSelected ? 'selected' : ''} ${isHovered ? 'hovered' : ''} ${!levelOk ? 'locked' : ''}`}
                    style={{ '--zone-color': zone.color }}
                    onClick={() => setSelectedZone(zone.id === selectedZone ? null : zone.id)}
                    onMouseEnter={() => setHoveredZone(zone.id)}
                    onMouseLeave={() => setHoveredZone(null)}
                  >
                    <div className="atlas-zone-icon">{zone.icon}</div>
                    <div className="atlas-zone-name">{zone.name}</div>
                    <div className={`atlas-zone-level ${fitClass}`} title={
                      fitClass === 'fit-danger' ? 'Dangerous — monsters here are above your level'
                      : fitClass === 'fit-trivial' ? 'Trivial — monsters here are far below your level'
                      : 'Good fit for your level'
                    }>L{zone.levelRange[0]}-{zone.levelRange[1]}</div>
                    {zone.isDiscovered && (
                      <div className="atlas-zone-progress">
                        <div className="atlas-zone-progress-bar">
                          <div className="atlas-zone-progress-fill" style={{ width: `${zone.exploration.pct}%`, background: zone.color }} />
                        </div>
                      </div>
                    )}
                    {isCurrent && <div className="atlas-zone-you">📍</div>}
                    {!zone.isDiscovered && !levelOk && <div className="atlas-zone-lock">🔒</div>}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Detail Panel */}
          <div className="atlas-detail">
            {selected ? (
              <>
                <div className="atlas-detail-header" style={{ borderColor: selected.color }}>
                  <span className="atlas-detail-icon">{selected.icon}</span>
                  <div>
                    <h3 className="atlas-detail-name">{selected.name}</h3>
                    <span className="atlas-detail-type" style={{ color: selected.color }}>
                      {typeLabels[selected.type] || selected.type}
                    </span>
                  </div>
                  {selected.isCurrent && <span className="atlas-detail-current">📍 You are here</span>}
                </div>

                <p className="atlas-detail-desc">{selected.description}</p>

                <div className="atlas-detail-info">
                  <div className="atlas-detail-row">
                    <span className="atlas-detail-label">Level Range</span>
                    <span className="atlas-detail-value" style={{
                      color: character.level < selected.levelRange[0] ? '#ef5350' :
                        character.level > selected.levelRange[1] ? '#888' : '#66bb6a'
                    }}>
                      {selected.levelRange[0]} — {selected.levelRange[1]}
                      {character.level < selected.levelRange[0] && ' ⚠️ Dangerous'}
                      {character.level > selected.levelRange[1] + 10 && ' (Trivial)'}
                    </span>
                  </div>

                  {selected.isDiscovered && (
                    <div className="atlas-detail-row">
                      <span className="atlas-detail-label">Explored</span>
                      <span className="atlas-detail-value">
                        {selected.exploration.explored} / {selected.exploration.total} maps ({selected.exploration.pct}%)
                      </span>
                    </div>
                  )}

                  {selected.tavern && (
                    <div className="atlas-detail-row">
                      <span className="atlas-detail-label">Tavern</span>
                      <span className="atlas-detail-value" style={{ color: '#c9a84c' }}>🍺 {selected.tavern}</span>
                    </div>
                  )}

                  {selected.features.length > 0 && (
                    <div className="atlas-detail-row">
                      <span className="atlas-detail-label">Features</span>
                      <span className="atlas-detail-value">
                        {selected.features.map(f => {
                          const featureLabels = {
                            tavern: '🍺 Tavern',
                            shops: '🏪 Shops',
                            temple: '⛪ Temple',
                            delve_entrance: '🌀 Delve Entrance'
                          };
                          return featureLabels[f] || f;
                        }).join(' · ')}
                      </span>
                    </div>
                  )}

                  <div className="atlas-detail-row">
                    <span className="atlas-detail-label">Connects to</span>
                    <span className="atlas-detail-value atlas-detail-connections">
                      {selected.connections.map(cId => {
                        const cZone = zones.find(z => z.id === cId);
                        if (!cZone) return null;
                        return (
                          <button
                            key={cId}
                            className="atlas-connection-link"
                            onClick={() => setSelectedZone(cId)}
                            style={{ color: cZone.color }}
                          >
                            {cZone.icon} {cZone.name}
                          </button>
                        );
                      })}
                    </span>
                  </div>
                </div>

                {!selected.isDiscovered && (
                  <div className="atlas-detail-undiscovered">
                    <span>🌫️</span> You haven't explored this zone yet.
                    {character.level < selected.levelRange[0] && (
                      <div style={{ marginTop: '4px', color: '#ef5350', fontSize: '0.8rem' }}>
                        Recommended level: {selected.levelRange[0]}+
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              <div className="atlas-detail-empty">
                <div className="atlas-detail-empty-icon">🗺️</div>
                <div>Select a zone to view details</div>
                <div className="atlas-detail-empty-hint">Click any zone on the map</div>
              </div>
            )}
          </div>
        </div>

        {/* Legend */}
        <div className="atlas-legend">
          <span className="atlas-legend-item"><span className="atlas-legend-dot" style={{ background: '#c9a84c' }} /> City</span>
          <span className="atlas-legend-item"><span className="atlas-legend-dot" style={{ background: '#4caf50' }} /> Wilderness</span>
          <span className="atlas-legend-item"><span className="atlas-legend-dot" style={{ background: '#5c6bc0' }} /> Dungeon</span>
          <span className="atlas-legend-item"><span className="atlas-legend-dot" style={{ background: '#ef5350' }} /> Boss</span>
          <span className="atlas-legend-item">📍 Current</span>
          <span className="atlas-legend-item">🔒 Locked</span>
        </div>
      </div>
    </div>
  );
};

export default AtlasPanel;
